import useWindowSize from '@hooks/use-window-size';
import { useCallback, useEffect, useRef } from 'react';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { Product } from '@interfaces/models/product';
import {
  getProductTrackingData,
  getRowCount,
  ImpressionProductDetails,
} from '@hooks/similar-products/use-similar-products-catalog-tracking';

const useHeroPpdImpressionTracking = (productFeedCategory?: string) => {
  const { isWiderThanLg, isWiderThanMd } = useWindowSize();
  const rowCountRef = useRef(getRowCount(isWiderThanLg, isWiderThanMd));

  useEffect(() => {
    rowCountRef.current = getRowCount(isWiderThanLg, isWiderThanMd);
  }, [isWiderThanLg, isWiderThanMd]);

  const { sendAnalyticEvent } = useAnalyticEvents('product');

  // this fn is used to send product impressions events on scroll
  const sendProductImpressionsEvent = useCallback(
    (productCardDetails: ImpressionProductDetails[]) => {
      if (productCardDetails.length === 0) {
        return;
      }

      const sendTrackingEvents = () =>
        sendAnalyticEvent('product_impression', { product_card_details: productCardDetails });

      // using requestIdleCallback to avoid blocking the main thread
      window.requestIdleCallback(sendTrackingEvents);
    },
    [sendAnalyticEvent],
  );

  const onRowReveal = useCallback(
    (index: number, productsOnRow: Product[]) => {
      const newProductData = productsOnRow.map((product, i) => ({
        ...getProductTrackingData(product),
        product_feed_category: productFeedCategory,
        product_position: String(index + i),
      }));
      sendProductImpressionsEvent(newProductData);
    },
    [productFeedCategory],
  );

  return { onRowReveal, rowCountRef };
};

export default useHeroPpdImpressionTracking;
